import { all, call, put, select, takeLatest } from "redux-saga/effects";
import api from "../../utils/api";
import { errorAlert, successAlert } from "../Alerts/actions";
import {
  fetchBillingPlans,
  fetchBillingPlansSuccess,
  fetchBillingPlansFailure,
  startBillingCheckoutSuccess,
  startBillingCheckoutFailure,
  confirmBillingCheckoutSuccess,
  confirmBillingCheckoutFailure,
  changeBillingPlanSuccess,
  changeBillingPlanFailure,
  previewBillingPlanChangeSuccess,
  previewBillingPlanChangeFailure,
  cancelBillingSuccess,
  cancelBillingFailure,
  updateBillingQuantitySuccess,
  updateBillingQuantityFailure,
} from "./actions";
import {
  FETCH_BILLING_PLANS,
  START_BILLING_CHECKOUT,
  CONFIRM_BILLING_CHECKOUT,
  CHANGE_BILLING_PLAN,
  PREVIEW_BILLING_PLAN_CHANGE,
  CANCEL_BILLING,
  UPDATE_BILLING_QUANTITY,
} from "./constants";

const asArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const alertError = (e, fallback) => {
  if (typeof e === "string") return errorAlert(e);
  if (typeof e?.error === "string") return errorAlert(e.error);
  if (typeof e?.message === "string") return errorAlert(e.message);
  return errorAlert(fallback);
};

export function* fetchBillingPlansSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const locationId = payload?.location_id;
  if (!token || !locationId) {
    return;
  }

  try {
    const [planRes, subRes] = yield all([
      call(api(token).get, `/api/billing/plans?location_id=${locationId}`),
      call(
        api(token).get,
        `/api/billing/subscription?location_id=${locationId}`,
      ),
    ]);
    const planPayload = planRes?.data ?? planRes;
    const plans = Array.isArray(planPayload)
      ? planPayload
      : asArray(planPayload?.plans);
    yield put(
      fetchBillingPlansSuccess({
        plans,
        subscriptions: asArray(subRes?.data ?? subRes),
        location: planPayload?.location ?? null,
        granted_plan_codes: Array.isArray(planPayload?.granted_plan_codes)
          ? planPayload.granted_plan_codes
          : [],
      }),
    );
  } catch (e) {
    yield put(fetchBillingPlansFailure());
    yield put(alertError(e, "Could not load plans"));
  }
}

export function* startBillingCheckoutSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const res = yield call(api(token).post, "/api/billing/checkout", payload);
    const data = res?.data ?? res;
    yield put(startBillingCheckoutSuccess(data));
    if (data?.checkout_url) {
      window.location.assign(data.checkout_url);
    } else {
      yield put(errorAlert("Stripe did not return a checkout URL."));
    }
  } catch (e) {
    yield put(startBillingCheckoutFailure());
    yield put(alertError(e, "Could not start checkout"));
  }
}

export function* confirmBillingCheckoutSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const res = yield call(api(token).post, "/api/billing/confirm", payload);
    yield put(confirmBillingCheckoutSuccess(res?.data ?? res));
    if (payload?.location_id) {
      yield put(fetchBillingPlans({ location_id: payload.location_id }));
    }
  } catch (e) {
    yield put(confirmBillingCheckoutFailure());
    yield put(alertError(e, "Checkout completed but activation is delayed"));
  }
}

export function* changeBillingPlanSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const res = yield call(
      api(token).post,
      "/api/billing/change-plan",
      payload,
    );
    const data = res?.data ?? res;
    yield put(changeBillingPlanSuccess(data));
    if (data?.checkout_url) {
      window.location.assign(data.checkout_url);
      return;
    }
    yield put(successAlert("Plan updated"));
    if (payload?.location_id) {
      yield put(fetchBillingPlans({ location_id: payload.location_id }));
    }
  } catch (e) {
    yield put(changeBillingPlanFailure());
    yield put(alertError(e, "Could not update plan"));
  }
}

export function* previewBillingPlanChangeSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const res = yield call(
      api(token).post,
      "/api/billing/change-plan/preview",
      payload,
    );
    yield put(previewBillingPlanChangeSuccess(res?.data ?? res));
  } catch (e) {
    yield put(previewBillingPlanChangeFailure());
    yield put(alertError(e, "Could not calculate the plan adjustment"));
  }
}

export function* cancelBillingSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const res = yield call(api(token).post, "/api/billing/cancel", payload);
    yield put(cancelBillingSuccess(res?.data ?? res));
    yield put(
      successAlert(
        payload?.resume
          ? "Subscription resumed"
          : "Cancel scheduled at period end",
      ),
    );
    if (payload?.location_id) {
      yield put(fetchBillingPlans({ location_id: payload.location_id }));
    }
  } catch (e) {
    yield put(cancelBillingFailure());
    yield put(alertError(e, "Could not update subscription"));
  }
}

export function* updateBillingQuantitySaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const res = yield call(api(token).post, "/api/billing/quantity", payload);
    yield put(updateBillingQuantitySuccess(res?.data ?? res));
    yield put(successAlert("Bed quantity updated"));
    if (payload?.location_id) {
      yield put(fetchBillingPlans({ location_id: payload.location_id }));
    }
  } catch (e) {
    yield put(updateBillingQuantityFailure());
    yield put(alertError(e, "Could not update beds"));
  }
}

export default function* BillingSaga() {
  yield all([
    takeLatest(FETCH_BILLING_PLANS, fetchBillingPlansSaga),
    takeLatest(START_BILLING_CHECKOUT, startBillingCheckoutSaga),
    takeLatest(CONFIRM_BILLING_CHECKOUT, confirmBillingCheckoutSaga),
    takeLatest(PREVIEW_BILLING_PLAN_CHANGE, previewBillingPlanChangeSaga),
    takeLatest(CHANGE_BILLING_PLAN, changeBillingPlanSaga),
    takeLatest(CANCEL_BILLING, cancelBillingSaga),
    takeLatest(UPDATE_BILLING_QUANTITY, updateBillingQuantitySaga),
  ]);
}
